import { AkpError, pickI18n, type PlanSummary } from '@akpl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api';
import { useAppConfig } from '../appConfig';
import { useAuth } from '../auth';
import { useMe } from '../hooks/useMe';
import { useLocale } from '../useT';
import { singOutcome, songAccess, type ViewerTier, viewerTier, visibleBadge } from './access';
import { webApi } from './api';
import { useCompanion, useWebSession } from './session/WebSessionProvider';
import { saveIntent, useWebUi, type WebSong } from './store';

/** `t` for the web namespace: `t('web:home.title')`. */
export function useWebT() {
  return useTranslation(['web', 'common']).t;
}

export { useLocale };

/** Song or playlist display text: editorial text is `{ en, es }`. */
export function useI18nText() {
  const locale = useLocale();
  return useCallback((text: { en: string; es: string }) => pickI18n(text, locale), [locale]);
}

export interface Viewer {
  tier: ViewerTier;
  plan: PlanSummary | undefined;
  /** A signed-in account whose `/me` has not answered yet: do not offer Premium actions. */
  loading: boolean;
  isAccount: boolean;
}

/** `me.plan`, never `features` (§5.2). */
export function useViewer(): Viewer {
  const auth = useAuth();
  const me = useMe();
  const plan = auth.isAccount ? me.data?.plan : undefined;
  return {
    tier: viewerTier(auth.isAccount, plan),
    plan,
    loading: auth.isAccount && me.isPending,
    isAccount: auth.isAccount,
  };
}

/** Domain and support links come from `/config`; never write them in (§1, Pendientes 3). */
export function useWebBase(): string {
  return useAppConfig().webBase;
}

export function useStripeKey(): string {
  return useAppConfig().stripePublishableKey;
}

export function useCardModalEnabled(): boolean {
  return useAppConfig().web?.cardModalEnabled ?? false;
}

export function useCheckoutEnabled(): boolean {
  return useAppConfig().web?.checkoutEnabled ?? false;
}

const FREE_KEY = ['web', 'free'] as const;

export function useFreeIds(): ReadonlySet<string> | undefined {
  const free = useQuery({
    queryKey: FREE_KEY,
    queryFn: () => api.webFree(),
    staleTime: 60_000,
  });
  const ids = free.data?.songIds;
  return useMemo(() => (ids ? new Set(ids) : undefined), [ids]);
}

/** What a song row or card paints: the badge, and "Cantar" or "Desbloquear". */
export function useSongAccess(song: WebSong) {
  const { tier } = useViewer();
  const freeIds = useFreeIds();
  const access = songAccess(song, freeIds);
  return {
    access,
    tier,
    badge: visibleBadge(tier, access),
    outcome: singOutcome(tier, access),
  };
}

export const LIBRARY_KEY = ['web', 'library'] as const;
const FAVORITE_IDS_KEY = ['web', 'favorites', 'ids'] as const;

/** Every ♥ on screen overlays this list, because catalog responses are caller-independent. */
export function useFavoriteIds(): ReadonlySet<string> {
  const auth = useAuth();
  const ids = useQuery({
    queryKey: [...FAVORITE_IDS_KEY, auth.user?.uid],
    queryFn: () => webApi.favoriteIds(),
    enabled: auth.isAccount,
    staleTime: 60_000,
    // §9: a ♥ set on another device shows up as soon as this tab comes back.
    refetchOnWindowFocus: 'always',
  });
  const songIds = ids.data?.songIds;
  return useMemo(() => new Set(songIds ?? []), [songIds]);
}

/**
 * ♥ with an optimistic update (§5.7): a failure reverts it and warns; removing offers "Deshacer"
 * for about 5 s, which repeats the `PUT`. A visitor or an anonymous guest gets E-06.
 */
export function useToggleFavorite() {
  const t = useWebT();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const showToast = useWebUi((s) => s.showToast);
  const openOverlay = useWebUi((s) => s.openOverlay);
  const key = [...FAVORITE_IDS_KEY, auth.user?.uid];

  const mutation = useMutation({
    mutationFn: ({ songId, on }: { songId: string; on: boolean }) =>
      on ? webApi.addFavorite(songId) : webApi.removeFavorite(songId),
    onMutate: async ({ songId, on }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ songIds: string[] }>(key);
      const rest = (previous?.songIds ?? []).filter((id) => id !== songId);
      queryClient.setQueryData(key, { songIds: on ? [songId, ...rest] : rest });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      queryClient.setQueryData(key, context?.previous);
      showToast({ message: t('web:common.favorites.failed'), tone: 'error' });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['web', 'favorites'] });
      void queryClient.invalidateQueries({ queryKey: LIBRARY_KEY });
    },
  });

  return useCallback(
    (songId: string, currentlyOn: boolean, title?: string) => {
      if (!auth.isAccount) {
        saveIntent({ type: 'favorite', songId });
        openOverlay({ kind: 'requireAccount' });
        return;
      }
      mutation.mutate({ songId, on: !currentlyOn });
      if (currentlyOn) {
        showToast({
          message: title
            ? t('web:library.favorites.removed', { title })
            : t('web:common.favorites.removed'),
          action: {
            label: t('web:common.actions.undo'),
            run: () => mutation.mutate({ songId, on: true }),
          },
        });
      }
    },
    [auth.isAccount, mutation, openOverlay, showToast, t],
  );
}

/**
 * The one entry point of "quiero cantar esto" (§5.1, §5.3, §5.4).
 *
 * In a party this phone is a remote, so it adds to the queue; otherwise the outcome decides between
 * the account flow, the paywall and the player. The player re-checks on the server: a free list
 * that changed with the page open answers `403 feature_required` there (§7.3).
 */
export function useSing() {
  const navigate = useNavigate();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const setPaywallAfterAuth = useWebUi((s) => s.setPaywallAfterAuth);
  const { tier } = useViewer();
  const freeIds = useFreeIds();
  const companion = useCompanion();
  const session = useWebSession();

  return useCallback(
    async (song: WebSong, tone?: string) => {
      if (companion.active) {
        await companion.addToQueue(song, tone);
        return;
      }
      const access = songAccess(song, freeIds);
      const outcome = singOutcome(tier, access);
      if (outcome === 'signup' || outcome === 'signup-paywall') {
        saveIntent({ type: 'sing', songId: song.id, ...(tone ? { tone } : {}) });
        setPaywallAfterAuth(outcome === 'signup-paywall');
        openOverlay({ kind: 'requireAccount' });
        return;
      }
      if (outcome === 'paywall') {
        saveIntent({ type: 'sing', songId: song.id, ...(tone ? { tone } : {}) });
        openOverlay({ kind: 'paywall', song });
        return;
      }
      if (session.mode === 'party') {
        await session.addToQueue(song, tone);
        return;
      }
      await navigate({
        to: '/cantar',
        search: { song: song.id, ...(tone ? { tone } : {}) },
      });
    },
    [companion, freeIds, navigate, openOverlay, session, setPaywallAfterAuth, tier],
  );
}

/** A refused action that means "this needs Premium" opens the paywall (§7.3). */
export function isPremiumRefusal(error: unknown): boolean {
  return error instanceof AkpError && error.code === 'feature_required';
}

export function usePlans() {
  return useQuery({
    queryKey: ['web', 'products'],
    queryFn: () => webApi.products(),
    staleTime: 5 * 60_000,
  });
}
